import { Divider, Stack, Typography } from '@mui/material';
import React, { ReactElement, useCallback, useMemo } from 'react';
import { useTheme } from 'styled-components';
import InfoDrawer from '../../abstract/InfoDrawer';
import InfoRow from '../../components/InfoRow';
import { useApp } from '../../contexts/AppContext';
import { enumKeyByValue, getAge } from '../../utils';
import { GetCycleResponseItem } from './types';
import { EthnicityType } from '../../types';

const PatientInfoDrawer: React.FC<{ cycle: GetCycleResponseItem, open: boolean, onClose: () => void }> = ({ cycle, open, onClose }) => {

    const patient = {
        ...cycle,
        name: cycle.patient,
        birth: new Date(cycle.birth),
        created: new Date(cycle.created),
        amh_date: cycle.amh_date ? new Date(cycle.amh_date) : null,
        fsh_date: cycle.fsh_date ? new Date(cycle.fsh_date) : null,
        afc_date: cycle.afc_date ? new Date(cycle.afc_date) : null,
        afc_left: cycle.afc_left ? Math.round(cycle.afc_left) : null,
        afc_right: cycle.afc_right ? Math.round(cycle.afc_right) : null,
        retrieval_date: cycle.retrieval_date ? new Date(cycle.retrieval_date) : null,
    };

    const drawerWidth = 500;
    const theme = useTheme();

      const { translate } = useApp();
      const translations = useMemo(() => translate("oocyte"), [translate]);

    const patientInfoRow = useCallback((label: string, value: string | number | ReactElement, date?: Date) => (
        <Stack gap={1}>
            <InfoRow valueFontWeight='normal' labelWidth={200} label={label} value={value} date={date} />
            <Divider />
        </Stack>
    ), []);

    const ethnicities = (patient.ethnicity ?? []).map((e) =>
        translate("general", `ethnicities.${enumKeyByValue(EthnicityType, e)}`)
    );
    
    return (
        <InfoDrawer
            open={open}
            onClose={onClose}
            cycle={cycle}
        >
            <Stack gap={2}>
                <Typography variant="report.headline" fontWeight={"bold"} gutterBottom>
                    {translations.patientInfo.sectionHeaders.patientDetails}
                </Typography>

                {/* Patient Information */}
                <Stack gap={theme.spacing(2)}>
                    <Typography fontWeight={"bold"} variant="report.title">
                        {translations.patientInfo.sectionHeaders.patientBackground}
                    </Typography>
                    <Stack gap={2}>
                        {patientInfoRow(translations.patientInfo.labels.name, patient.name)}
                        {patientInfoRow(translations.patientInfo.labels.age, getAge(patient.birth))}
                        {patientInfoRow(translations.patientInfo.labels.height, `${patient.height} cm`)}
                        {patientInfoRow(translations.patientInfo.labels.weight, `${patient.weight} kg`)}
                        {patientInfoRow(translations.patientInfo.labels.ethnicity, ethnicities.join(', '))}
                    </Stack>
                </Stack>
            </Stack>
            <Stack gap={2} sx={{ marginTop: theme.spacing(6) }}>
                <Typography fontWeight={"bold"} variant="report.title">
                    {translations.patientInfo.sectionHeaders.ovarianReserveMarkers}
                </Typography>
                <Typography fontWeight={"bold"} variant="report.title">
                    {translations.patientInfo.sectionHeaders.amhFshLevel}
                </Typography>
                <Stack gap={2}>
                    {patientInfoRow('AMH Level', patient.amh_value === null ? "" :  `${patient.amh_value} ng/mL`)}
                    {patientInfoRow('AMH test date', (patient.amh_date === null) ? '' : `${patient.amh_date?.toLocaleDateString()}`)}
                    {patientInfoRow('FSH Level', (patient.fsh_value === null) ? '' : `${patient.fsh_value} mIU/mL`)}
                    {patientInfoRow('FSH test date', (patient.fsh_date === null) ? '' : `${patient.fsh_date?.toLocaleDateString()}`)}
                </Stack>
            </Stack>
            <Stack gap={2}>
                <Typography fontWeight={"bold"} variant="report.title">
                    {translations.patientInfo.sectionHeaders.afc}
                </Typography>
                <Stack gap={2}>
                    {patientInfoRow(translations.patientInfo.labels.rightOvary, (patient.afc_right === null) ? '' : `${patient.afc_right} follicles`)}
                    {patientInfoRow(translations.patientInfo.labels.leftOvary, (patient.afc_left === null) ? '' : `${patient.afc_left} follicles`)}
                    {patientInfoRow(translations.patientInfo.labels.afcUltrasoundDate, (patient.afc_date === null) ? '' : patient.afc_date.toLocaleDateString())}
                </Stack>
            </Stack>
            <Stack gap={2}>
                <Typography fontWeight={"bold"} variant="report.title">
                    {translations.patientInfo.sectionHeaders.cycleDetails}
                </Typography>
                <Stack gap={2}>
                    {patientInfoRow(translations.patientInfo.labels.retrievalDate, (patient.retrieval_date === null) ? '' : patient.retrieval_date.toLocaleDateString())}
                    {patientInfoRow(translations.patientInfo.labels.numberOfRetrievedOocytes, patient.retrieval_value ?? patient.images.length)}
                </Stack>
            </Stack>
        </InfoDrawer>
    );
};

export default PatientInfoDrawer;
